/**
 * Position-computation helpers for InfrastructureMapCanvas.
 *
 * Three families:
 *   1. computeCollapsedPositions: repositions visible siblings/descendants
 *      around a collapsed parent in tree mode.
 *   2. computeBusCompressedPositions: evenly spaces nodes along a bus when
 *      some endpoints in the component are collapsed.
 *   3. buildNodesToRender: produces the final list of renderable nodes,
 *      choosing between drag, bus, collapsed, intrinsic, or fallback grid
 *      positions in priority order.
 */

import type {
  CanvasPosition,
  EndpointDisplayInfo,
  MapStyle,
  SummaryTileData,
} from '../../types/infrastructure-map'
import type { EndpointRelationship } from '../../types/case-container'
import type { RenderableNode } from './infrastructure-map-canvas.helpers'
import {
  buildConfirmedAdjacency,
  findConnectedComponents,
} from './infrastructure-map-canvas.helpers'

const CHILD_SPACING = 160
const SUMMARY_TILE_WIDTH = 100
const DESCENDANT_SPACING = 160
const DESCENDANT_Y_OFFSET = 120
const FAR_THRESHOLD = 300

interface CollapseCtx {
  displayInfo: Map<string, EndpointDisplayInfo>
  parentChildMap: Map<string, string[]>
  positions: Map<string, CanvasPosition>
  offsets: Map<string, number>
}

interface DescendantCtx {
  displayInfo: Map<string, EndpointDisplayInfo>
  parentChildMap: Map<string, string[]>
  positions: Map<string, CanvasPosition>
}

export function computeCollapsedPositions(
  summaryTiles: Map<string, SummaryTileData>,
  displayInfo: Map<string, EndpointDisplayInfo>,
  parentChildMap: Map<string, string[]>
): Map<string, CanvasPosition> {
  const ctx: CollapseCtx = {
    displayInfo,
    parentChildMap,
    positions: new Map<string, CanvasPosition>(),
    offsets: new Map<string, number>(),
  }

  for (const [parentId, summaryData] of summaryTiles) {
    repositionVisibleChildrenOfCollapsedParent(parentId, summaryData, ctx)
  }

  for (const [nodeId, xOffset] of ctx.offsets) {
    const parentPos = ctx.positions.get(nodeId)
    if (!parentPos) continue
    if (xOffset !== 0) {
      propagateOffsetToDescendants(nodeId, xOffset, ctx)
    } else {
      positionDescendantsRelativeToParent(nodeId, parentPos, ctx)
    }
  }

  applyAdditionalFarChildPass(ctx)

  return ctx.positions
}

function repositionVisibleChildrenOfCollapsedParent(
  parentId: string,
  summaryData: SummaryTileData,
  ctx: CollapseCtx
): void {
  const { displayInfo, parentChildMap, positions, offsets } = ctx
  const parentInfo = displayInfo.get(parentId)
  if (!parentInfo?.position) return

  const allDirectChildren = parentChildMap.get(parentId) ?? []
  const visibleChildren = allDirectChildren.filter((childId) => {
    const childInfo = displayInfo.get(childId)
    return childInfo && !childInfo.isHiddenByCollapse
  })

  if (visibleChildren.length === 0 && summaryData.directChildCount === 0) return

  let currentX = parentInfo.position.x
  if (summaryData.directChildCount > 0) currentX += SUMMARY_TILE_WIDTH

  for (const childId of visibleChildren) {
    const childInfo = displayInfo.get(childId)
    if (!childInfo?.position) continue
    const newX = currentX
    positions.set(childId, { x: newX, y: childInfo.position.y })
    offsets.set(childId, newX - childInfo.position.x)
    currentX += CHILD_SPACING
  }
}

function getAllDescendants(
  nodeId: string,
  parentChildMap: Map<string, string[]>,
  visited: Set<string> = new Set()
): string[] {
  if (visited.has(nodeId)) return []
  visited.add(nodeId)
  const children = parentChildMap.get(nodeId) ?? []
  const descendants: string[] = [...children]
  for (const childId of children) {
    descendants.push(...getAllDescendants(childId, parentChildMap, visited))
  }
  return descendants
}

function propagateOffsetToDescendants(nodeId: string, xOffset: number, ctx: DescendantCtx): void {
  const { parentChildMap, displayInfo, positions } = ctx
  const directChildren = parentChildMap.get(nodeId) ?? []
  for (const childId of directChildren) {
    if (positions.has(childId)) continue
    const childInfo = displayInfo.get(childId)
    if (!childInfo?.position) continue
    positions.set(childId, { x: childInfo.position.x + xOffset, y: childInfo.position.y })
    for (const grandchildId of getAllDescendants(childId, parentChildMap)) {
      if (positions.has(grandchildId)) continue
      const gcInfo = displayInfo.get(grandchildId)
      if (gcInfo?.position) {
        positions.set(grandchildId, { x: gcInfo.position.x + xOffset, y: gcInfo.position.y })
      }
    }
  }
}

function positionDescendantsRelativeToParent(
  parentId: string,
  parentPos: CanvasPosition,
  ctx: DescendantCtx
): void {
  const { parentChildMap, displayInfo, positions } = ctx
  const directChildren = parentChildMap.get(parentId) ?? []
  const childrenToPosition: string[] = []
  for (const childId of directChildren) {
    if (positions.has(childId)) continue
    const childInfo = displayInfo.get(childId)
    if (childInfo?.position) childrenToPosition.push(childId)
  }
  if (childrenToPosition.length === 0) return

  const totalWidth = (childrenToPosition.length - 1) * DESCENDANT_SPACING
  const startX = parentPos.x - totalWidth / 2

  childrenToPosition.forEach((childId, index) => {
    const newPos: CanvasPosition = {
      x: startX + index * DESCENDANT_SPACING,
      y: parentPos.y + DESCENDANT_Y_OFFSET,
    }
    positions.set(childId, newPos)
    positionDescendantsRelativeToParent(childId, newPos, ctx)
  })
}

function applyAdditionalFarChildPass(ctx: CollapseCtx): void {
  const { displayInfo, parentChildMap, offsets, positions } = ctx
  for (const [nodeId, nodeInfo] of displayInfo) {
    if (nodeInfo.isHiddenByCollapse) continue
    if (offsets.has(nodeId)) continue
    const nodePos = positions.get(nodeId) ?? nodeInfo.position
    if (!nodePos) continue
    const children = parentChildMap.get(nodeId) ?? []
    if (children.length === 0) continue
    if (hasFarChild(children, nodePos, displayInfo)) {
      positionDescendantsRelativeToParent(nodeId, nodePos, ctx)
    }
  }
}

function hasFarChild(
  children: string[],
  nodePos: CanvasPosition,
  displayInfo: Map<string, EndpointDisplayInfo>
): boolean {
  for (const childId of children) {
    const childInfo = displayInfo.get(childId)
    if (!childInfo || childInfo.isHiddenByCollapse) continue
    if (!childInfo.position) continue
    if (Math.abs(childInfo.position.x - nodePos.x) > FAR_THRESHOLD) return true
  }
  return false
}

const BUS_NODE_SPACING = 160
const BUS_FALLBACK_X = 400

interface BusCtx {
  displayInfo: Map<string, EndpointDisplayInfo>
  adjacency: Map<string, Set<string>>
  positions: Map<string, CanvasPosition>
}

export function computeBusCompressedPositions(
  mapStyle: MapStyle,
  displayInfo: Map<string, EndpointDisplayInfo>,
  relationships: EndpointRelationship[]
): Map<string, CanvasPosition> {
  const positions = new Map<string, CanvasPosition>()
  if (mapStyle !== 'bus') return positions

  const adjacency = buildConfirmedAdjacency(relationships)
  const ctx: BusCtx = { displayInfo, adjacency, positions }
  for (const component of findConnectedComponents(adjacency)) {
    compressComponentForBus(component, ctx)
  }
  return positions
}

interface BusNodeInfo {
  endpointId: string
  position: CanvasPosition | null
  isCollapsed: boolean
  connections: number
}

function gatherBusNodeInfo(
  component: string[],
  displayInfo: Map<string, EndpointDisplayInfo>,
  adjacency: Map<string, Set<string>>
): BusNodeInfo[] {
  return component.map((endpointId) => {
    const info = displayInfo.get(endpointId)
    return {
      endpointId,
      position: info?.position ?? null,
      isCollapsed: info?.isCollapsed ?? false,
      connections: adjacency.get(endpointId)?.size ?? 0,
    }
  })
}

function findHubIndex(allNodesInfo: BusNodeInfo[]): number {
  let hubIndex = 0
  let maxConnections = 0
  for (const [idx, node] of allNodesInfo.entries()) {
    if (node.connections > maxConnections) {
      maxConnections = node.connections
      hubIndex = idx
    }
  }
  return hubIndex
}

function compressComponentForBus(component: string[], ctx: BusCtx): void {
  const { displayInfo, adjacency, positions } = ctx
  const allNodesInfo = gatherBusNodeInfo(component, displayInfo, adjacency)
  if (allNodesInfo.length === 0) return

  const hubIndex = findHubIndex(allNodesInfo)
  const hubInfo = allNodesInfo[hubIndex]
  if (!hubInfo?.position) return

  const hasCollapsed = allNodesInfo.some((node, idx) => idx !== hubIndex && node.isCollapsed)
  if (!hasCollapsed) {
    for (const nodeData of allNodesInfo) {
      if (nodeData.position) positions.set(nodeData.endpointId, nodeData.position)
    }
    return
  }

  applyEvenBusSpacing(allNodesInfo, hubIndex, positions)
}

interface VisibleBusNode {
  endpointId: string
  originalPosition: CanvasPosition
  isHub: boolean
}

function applyEvenBusSpacing(
  allNodesInfo: BusNodeInfo[],
  hubIndex: number,
  positions: Map<string, CanvasPosition>
): void {
  const visibleNodeInfo: VisibleBusNode[] = []
  for (const [i, nodeInfo] of allNodesInfo.entries()) {
    const isHub = i === hubIndex
    if (isHub && nodeInfo.position) {
      visibleNodeInfo.push({
        endpointId: nodeInfo.endpointId,
        originalPosition: nodeInfo.position,
        isHub: true,
      })
    } else if (!nodeInfo.isCollapsed && nodeInfo.position) {
      visibleNodeInfo.push({
        endpointId: nodeInfo.endpointId,
        originalPosition: nodeInfo.position,
        isHub: false,
      })
    }
  }

  visibleNodeInfo.sort((nodeA, nodeB) => nodeA.originalPosition.x - nodeB.originalPosition.x)
  const hubIdx = visibleNodeInfo.findIndex((node) => node.isHub)
  const hubX =
    hubIdx !== -1 ? (visibleNodeInfo[hubIdx]?.originalPosition.x ?? BUS_FALLBACK_X) : BUS_FALLBACK_X

  for (const [i, nodeData] of visibleNodeInfo.entries()) {
    const offsetFromHub = i - Math.max(hubIdx, 0)
    positions.set(nodeData.endpointId, {
      x: hubX + offsetFromHub * BUS_NODE_SPACING,
      y: nodeData.originalPosition.y,
    })
  }
}

const FALLBACK_GRID_COLS = 6
const FALLBACK_GRID_X_START = 200
const FALLBACK_GRID_Y_START = 150
const FALLBACK_GRID_X_STEP = 180
const FALLBACK_GRID_Y_STEP = 140

export interface RenderCtx {
  displayInfo: Map<string, EndpointDisplayInfo>
  mapStyle: MapStyle
  dragPositions: Map<string, CanvasPosition>
  collapsedPositions: Map<string, CanvasPosition>
  busCompressedPositions: Map<string, CanvasPosition>
}

export function buildNodesToRender(
  ctx: RenderCtx,
  relationships: EndpointRelationship[]
): RenderableNode[] {
  const { displayInfo, mapStyle } = ctx
  const busHubIds = mapStyle === 'bus' ? findBusHubIds(relationships) : new Set<string>()
  const nodes: RenderableNode[] = []
  let nodeIndex = 0

  for (const [endpointId, info] of displayInfo) {
    if (info.isHiddenByCollapse) continue
    if (mapStyle === 'bus' && info.isCollapsed && !busHubIds.has(endpointId)) continue
    if (info.displayType !== 'tile' && info.displayType !== 'dot') continue

    const position = pickNodePosition({ endpointId, info, nodeIndex }, ctx)
    nodes.push({ endpointId, displayType: info.displayType, position, tileSize: info.tileSize })
    nodeIndex++
  }
  return nodes
}

function findBusHubIds(relationships: EndpointRelationship[]): Set<string> {
  const connectionCount = new Map<string, number>()
  for (const rel of relationships) {
    if (rel.status !== 'confirmed') continue
    connectionCount.set(rel.sourceEndpointId, (connectionCount.get(rel.sourceEndpointId) ?? 0) + 1)
    connectionCount.set(rel.targetEndpointId, (connectionCount.get(rel.targetEndpointId) ?? 0) + 1)
  }
  let maxConnections = 0
  for (const [, count] of connectionCount) if (count > maxConnections) maxConnections = count
  const hubIds = new Set<string>()
  for (const [id, count] of connectionCount) if (count === maxConnections) hubIds.add(id)
  return hubIds
}

interface NodePickEntry {
  endpointId: string
  info: EndpointDisplayInfo
  nodeIndex: number
}

function pickNodePosition(entry: NodePickEntry, ctx: RenderCtx): CanvasPosition {
  const { endpointId, info, nodeIndex } = entry
  const { mapStyle, dragPositions, collapsedPositions, busCompressedPositions } = ctx
  const dragPos = dragPositions.get(endpointId)
  if (dragPos) return dragPos
  const busPos = busCompressedPositions.get(endpointId)
  if (mapStyle === 'bus' && busPos) return busPos
  const collapsedPos = collapsedPositions.get(endpointId)
  if (collapsedPos) return collapsedPos
  if (info.position) return info.position
  const col = nodeIndex % FALLBACK_GRID_COLS
  const row = Math.floor(nodeIndex / FALLBACK_GRID_COLS)
  return {
    x: FALLBACK_GRID_X_START + col * FALLBACK_GRID_X_STEP,
    y: FALLBACK_GRID_Y_START + row * FALLBACK_GRID_Y_STEP,
  }
}
